import React, { useState, forwardRef, memo, useMemo } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import BedWithPatientInfo from '../../components/RevampBedStatusIcon/RevampStatusBed/BedWithPatientInfo';
import { isNotEmptyArray, positionRemaks, showPopOptimized } from '../../../../utils/newBedStatusUtil';
import RemaksIcon from '../../components/RevampBedStatusIcon/RemaksIcon';
import { MdroIcon } from '../../../../components/MdroInfo';
import { bedP } from '../../bsApp';
import { bedStatusAPI } from '../../../../apis';
import { alertMsg, ResponseCommCodeEnum } from '../../../../apis/commlogic';
import { i18n } from '../../../../language';
import { CmsAlertStandardIcon, DropUpIcon } from '../../../../components/Icon';
import RevampStatusBed from '../../components/RevampBedStatusIcon/RevampStatusBed';
import { bsMlookupListFunc } from '../../bsEnumeration';
import useCheckLogin from '../../../../utils/useCheckLogin';
import { getArrayRandomKey } from '../../../../utils';
import { PendingAssignSvgIcon, WarningIcon } from './SvgIcon';
import PendingAssignPopup from './PendingAssignPopup';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    display: 'flex',
  },
  queueBarToggle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '1rem',
    color: '#7d86a9',
    backgroundColor: '#FFFFFF',
    padding: '1rem 0.25rem',
    height: 'fit-content',
    cursor: 'pointer',
    boxShadow: '10px 0 20px 0px rgba(198, 211, 230, 0.4)',
    borderRadius: '0 8px 8px 0',
  },
  queueBarArrow: ({ showQueueBar }) => ({
    // width: '100%',
    // height: '1rem',
    transform: showQueueBar ? 'rotate(-90deg)' : 'rotate(90deg)',
  }),
  queueBarText: {
    fontSize: '1rem',
    whiteSpace: 'nowrap',
    transform: 'rotate(180deg)' /* 文字竖向排列 */,
    writingMode: 'vertical-rl' /* 支持竖排文字（现代浏览器）*/,
    textOrientation: 'mixed',
    fontFamily: 'NotoSans-Medium',
  },
  dropIcon: {
    fontSize: '0.875rem',
    color: '#538fff',
  },
  queueBarContent: ({ showRemarkBar }) => ({
    padding: `0.571rem 0.571rem ${showRemarkBar ? '2.5rem' : '0.571rem'}`,
    height: '100%',
    backgroundColor: '#FFF',
    overflowY: 'scroll',
    scrollbarWidth: 0,
    boxShadow: '10px 0 20px 0px rgba(198, 211, 230, 0.4)',
    maxWidth: '8.142rem',
    borderRadius: '0 0 8px',
    '&::-webkit-scrollbar': {
      display: 'none' /* Chrome, Safari, Edge */,
    },
    display: 'flex',
    flexDirection: 'column',
    gridGap: '2.5rem',
  }),
  queueList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.714rem',
  },
  patientInfo: {
    paddingLeft: '0.642rem',
    backgroundColor: '#BAD2FF',
    flex: 1,
    width: '7.857rem',
    position: 'relative',
    borderRadius: '4px',
    overflow: 'hidden',

    '&::before': {
      position: 'absolute',
      top: '0',
      left: '0',
      content: '""',
      display: 'block',
      width: '0.285rem',
      height: '100%',
      backgroundColor: '#1A6AFF',
      zIndex: '2',
    },
  },
  bedNo: {
    color: '#1A6AFF',
    fontSize: '1rem',
    fontWeight: 700,
  },
  patientNo: {
    fontSize: '0.714rem',
    color: '#1A6AFF',
    fontWeight: 700,
  },
  divider: {
    height: '2px',
    width: '100%',
    margin: '1rem 0',
    background: '#E4E7EB',
  },
  queueTitle: {
    color: '#7D86A9',
    fontSize: '1rem',
    fontWeight: 500,
  },
  queueItem: () => ({
    position: 'relative',
    padding: '0.428rem',
    display: 'flex',
    gap: '0.428rem',
    alignItems: 'center',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '8px',
  }),
  cmsAlert: {
    width: '1.85rem',
    height: '1.6rem',
    marginLeft: '0.15rem',
    marginTop: '-0.2rem',
  },
}));

const QueueBarComponent = forwardRef(
  (
    {
      showPatientName,
      bedStatusQueueList,
      getFloorPlanListAPI,
      BsMlookupList,
      showRemarkBar,
      showQueueBar,
      setShowQueueBar,
      filter,
      readOnly = false,
    },
    ref
  ) => {
    const classes = useStyles({ showQueueBar, showRemarkBar });

    const groupQueueByLocation = (bedStatusQueueList) => {
      const pendingList = bedStatusQueueList?.nonAssigns ?? [];
      const dischargeList = bedStatusQueueList?.dischs ?? [];
      const groupedMap = new Map();

      const processItem = (item, listType) => {
        if (!item) return;
        const location = item.ccWardLocation;
        // Skip if location is null or undefined
        if (location === null || location === undefined) return;

        if (!groupedMap.has(location)) {
          groupedMap.set(location, {
            ccWardLocation: location,
            pendingAssignList: [],
            discharingList: [],
          });
        }

        const group = groupedMap.get(location);
        if (listType === 'pending') {
          group.pendingAssignList.push(item);
        } else if (listType === 'discharge') {
          group.discharingList.push(item);
        }
      };

      if (Array.isArray(pendingList)) {
        pendingList.forEach((item) => processItem(item, 'pending'));
      }
      if (Array.isArray(dischargeList)) {
        dischargeList.forEach((item) => processItem(item, 'discharge'));
      }

      return _.orderBy(Array.from(groupedMap.values()), 'ccWardLocation');
    };
    const groupedQueueList = useMemo(() => groupQueueByLocation(bedStatusQueueList), [bedStatusQueueList]);

    const isMutipleWardCode = bedStatusQueueList?.multipWardCd ?? false;
    const history = useHistory();
    const [showPAPopup, setShowPAPopup] = useState(false);
    const [pendingAssignPatient, setPendingAssignPatient] = useState({});
    const [showDischargePopup, setShowDischargePopup] = useState(false);
    const [dischargingInfo, setDischargingInfo] = useState({});
    const checkLogin = useCheckLogin();

    const getStatusIcons = (item) => {
      const statusIcons = positionRemaks(item, false, RemaksIcon);
      const lefts = statusIcons.left() ?? [];
      const rights = statusIcons.right() ?? [];
      const tops = statusIcons.top() ?? [];
      return { lefts, rights, tops };
    };

    const handleShowDischargePopup = (item) => {
      setDischargingInfo(item);
      setShowDischargePopup(true);
    };

    const DischargingItem = ({ item }) => {
      const { lefts, rights, tops } = getStatusIcons(item);
      const handleOnclick = (item) => {
        if (readOnly) return;
        if (!checkLogin()) {
          return;
        }
        handleShowDischargePopup(item);
      };

      return (
        <Box onClick={() => handleOnclick(item)} style={{ cursor: 'pointer' }}>
          {(isNotEmptyArray(lefts) || isNotEmptyArray(rights)) && (
            <Box height="1.142rem" display="flex" mb="0.1rem">
              {lefts}
              {rights}
              {tops}
            </Box>
          )}
          <Box display="flex">
            <Box>
              <Box ml={showPatientName ? '-0.357rem' : '-0.5rem'}>
                <RevampStatusBed
                  genderType={item?.patGender}
                  bedNo={item?.bedNo}
                  ward={item?.ccWardCd}
                  allData={item}
                  showPatientName={showPatientName}
                  isVertical={true}
                  status={item?.status}
                  isMutipleWardCode={isMutipleWardCode}
                />
              </Box>
              <Box fontSize="0.857rem" fontWeight={500}>
                {item?.[bedP.dischargeDestination]
                  ? bsMlookupListFunc.getTypeLookupName(
                      BsMlookupList,
                      'bs_dischg_dest',
                      item?.[bedP.dischargeDestination]
                    )
                  : `${item?.[bedP.transferOutRequestInfo]?.destHospCode} ${
                      item?.[bedP.transferOutRequestInfo]?.destWardCode
                    }`}
              </Box>
            </Box>
            {(isNotEmptyArray(item?.[bedP.mdroInfo]) || isNotEmptyArray(item?.[bedP.cmsAlertInfos])) && (
              <Box minWidth="1.142rem" display="flex" flexDirection="column" zIndex={9}>
                {_.map(_.orderBy(item?.[bedP.mdroInfo], ['alertDesc'], ['asc']), (item, index) => (
                  <MdroIcon
                    value={item.alertDesc}
                    confirmedInd={item.confirmedInd}
                    width="2rem"
                    height="1.142rem"
                    fontSize="0.642rem"
                    type="bedstatus"
                    key={`${item.rowkey}-${index}`}
                  />
                ))}
                {isNotEmptyArray(item?.[bedP.cmsAlertInfos]) && <CmsAlertStandardIcon className={classes.cmsAlert} />}
              </Box>
            )}
          </Box>
        </Box>
      );
    };

    const PendingAssignWithNoName = ({ bedNo, ward, sex, showWarningIcon }) => {
      const borderColor = sex === 'F' ? '#F54C9D' : '#6385C0';
      const bgColor = sex === 'F' ? '#FFEAF1' : '#F1F5FB';

      return (
        <Box display="flex">
          <Box position="relative" maxWidth="max-content">
            {showWarningIcon && (
              <Box position="absolute" left="-0.607rem" top="-0.5rem" zIndex={9}>
                <WarningIcon warningColor="#951B07" width="1.214rem" height="1rem" />
              </Box>
            )}
            <Box className={classes.queueItem} borderColor={borderColor} style={{ background: bgColor }}>
              <PendingAssignSvgIcon gender={sex} />
              {bedNo && (
                <Box fontWeight={700} color={borderColor} textAlign="center">
                  <Box fontSize="1.142rem" lineHeight={1}>
                    {bedNo ?? ''}
                  </Box>
                  <Box fontSize="0.714rem" lineHeight={1.1}>
                    {ward ?? ''}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      );
    };

    const handleShowPAPopup = (patientInfo) => {
      setPendingAssignPatient(patientInfo);
      setShowPAPopup(true);
    };

    const PendingAssignItem = ({ item }) => {
      const { bedNo, ccWardCd, caseNo = '', patInfo, patGender, eAdmInfo } = item;
      // 当eAdmInfo.priority 有值并且大于0的时候才显示warning icon
      const showWarningIcon = Boolean(eAdmInfo?.priority);
      const style = {
        rootBg: patGender === 'F' ? '#FFD8E3' : '#BAD2FF',
        bedNoFont: patGender === 'F' ? '#FF50A4' : '#1A6AFF',
      };
      const { lefts, rights, tops } = getStatusIcons(item);
      const handleOnclick = (item) => {
        if (readOnly) return;
        if (!checkLogin()) {
          return;
        }
        handleShowPAPopup(item);
      };

      return (
        <Box onClick={() => handleOnclick(item)} style={{ cursor: 'pointer' }}>
          {(isNotEmptyArray(lefts) || isNotEmptyArray(rights) || isNotEmptyArray(tops)) && (
            <Box height="1.142rem" display="flex" mb="0.1rem">
              {lefts}
              {rights}
              {tops}
            </Box>
          )}
          <Box display="flex">
            {showPatientName ? (
              <Box ml="-0.357rem">
                <BedWithPatientInfo
                  patInfo={patInfo}
                  caseNo={caseNo}
                  style={style}
                  isQueueBar={true}
                  displayWardNo={ccWardCd ?? ''}
                  displayBedNo={bedNo ?? ''}
                />
              </Box>
            ) : (
              <PendingAssignWithNoName
                bedNo={bedNo ?? ''}
                ward={ccWardCd ?? ''}
                sex={patGender}
                showWarningIcon={showWarningIcon}
              />
            )}
            {(isNotEmptyArray(item?.[bedP.mdroInfo]) || isNotEmptyArray(item?.[bedP.cmsAlertInfos])) && (
              <Box minWidth="1.142rem" display="flex" flexDirection="column" zIndex={9}>
                {_.map(_.orderBy(item?.[bedP.mdroInfo], ['alertDesc'], ['asc']), (item, index) => (
                  <MdroIcon
                    value={item.alertDesc}
                    confirmedInd={item.confirmedInd}
                    width="2rem"
                    height="1.142rem"
                    fontSize="0.642rem"
                    type="bedstatus"
                    key={`${item.rowkey}-${index}`}
                  />
                ))}
                {isNotEmptyArray(item?.[bedP.cmsAlertInfos]) && <CmsAlertStandardIcon className={classes.cmsAlert} />}
              </Box>
            )}
          </Box>
        </Box>
      );
    };

    const goToEAdmissionForm = async () => {
      const eaId = pendingAssignPatient?.eAdmInfo?.eaId;
      if (!eaId) {
        return;
      }
      history.push(`/bsAdmissionDetail/${eaId}`);
    };

    const updateBedStatus = async (data) => {
      try {
        await bedStatusAPI.postUpdateStatus(data);
        try {
          await getFloorPlanListAPI();
        } catch {}
        setShowDischargePopup(false);
      } catch (e) {
        if (e.statusCode === ResponseCommCodeEnum.BED_VERSION) {
          await getFloorPlanListAPI();
          alertMsg(
            i18n.t(
              'BEDSTATUS_BED_STATUS_OF_THIS_BED_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_OR_IPAS_PLEASE_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_BED_STATUS_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
            ),
            _.uniqueId('warning_'),
            'warning'
          );
        }
      }
    };
    const saveBedStatus = async (data) => {
      try {
        await bedStatusAPI.postSaveRemark({ ...data, ccWardCd: data.wardCd });
        try {
          await getFloorPlanListAPI();
        } catch {}
        setShowDischargePopup(false);
      } catch (e) {
        if (e.statusCode === ResponseCommCodeEnum.BED_VERSION) {
          await getFloorPlanListAPI();
          alertMsg(
            i18n.t(
              'BEDSTATUS_BED_STATUS_OF_THIS_BED_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_OR_IPAS_PLEASE_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_BED_STATUS_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
            ),
            _.uniqueId('warning_'),
            'warning'
          );
        }
      }
    };
    const saveReturnDayCase = async (data) => {
      try {
        await bedStatusAPI.postSaveReturnDayCase(data);
        try {
          await getFloorPlanListAPI();
        } catch (error) {}
        setShowDischargePopup(false);
      } catch (error) {}
    };

    const EmptyQueueBar = () => {
      return (
        <Box>
          <Box className={classes.queueList}>
            <Box className={classes.queueTitle}>Queue (0)</Box>
          </Box>
          <Box className={classes.divider} />
          <Box className={classes.queueList}>
            <Box className={classes.queueTitle}>Discharging or T/O (0)</Box>
          </Box>
        </Box>
      );
    };

    return (
      <Box className={classes.container} ref={ref}>
        {showQueueBar && (
          <Box className={classes.queueBarContent}>
            {Array.isArray(groupedQueueList) && groupedQueueList?.length > 0 ? (
              _.map(groupedQueueList, (groupItem, groupIndex) => {
                const pendings = groupItem?.pendingAssignList || [];
                const discharings = groupItem?.discharingList || [];
                const isShow = filter?.wardLocation === 'All' || groupItem?.ccWardLocation === filter?.wardLocation;

                return isShow ? (
                  <Box>
                    <Box
                      fontSize="1.5rem"
                      mb="0.857rem"
                      fontWeight="bold"
                      key={getArrayRandomKey(groupIndex, `group-${groupItem?.ccWardLocation}`)}
                    >
                      {groupItem?.ccWardLocation}
                    </Box>

                    <Box className={classes.queueList}>
                      <Box className={classes.queueTitle}>Queue ({pendings?.length ?? 0})</Box>
                      {Array.isArray(pendings) && pendings?.length !== 0 && (
                        <Box className={classes.queueList}>
                          {_.map(pendings, (item, index) => (
                            <PendingAssignItem item={item} key={`pending-${index}-${item?.caseNo}`} />
                          ))}
                        </Box>
                      )}
                    </Box>

                    <Box className={classes.divider} />

                    <Box className={classes.queueList}>
                      <Box className={classes.queueTitle}>Discharging or T/O ({discharings?.length ?? 0})</Box>
                      {Array.isArray(discharings) && discharings?.length !== 0 && (
                        <Box className={classes.queueList}>
                          {_.map(discharings, (item, id) => (
                            <DischargingItem item={item} key={`discharging-${id}-${item?.caseNo}`} />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <EmptyQueueBar />
                );
              })
            ) : (
              <EmptyQueueBar />
            )}
          </Box>
        )}

        <Box
          className={classes.queueBarToggle}
          onClick={() => {
            setShowQueueBar(!showQueueBar);
            sessionStorage.setItem('SHOW_BED_STATUS_QUEUE_BAR', !showQueueBar);
          }}
        >
          <Box className={classes.queueBarArrow}>
            <DropUpIcon className={classes.dropIcon} />
          </Box>
          <Box className={classes.queueBarText}>Queue</Box>
        </Box>

        {showPAPopup && (
          <PendingAssignPopup
            open={showPAPopup}
            closePop={() => setShowPAPopup(false)}
            patientInfo={pendingAssignPatient}
            goToEAdmissionForm={goToEAdmissionForm}
          />
        )}
        {showDischargePopup &&
          showPopOptimized({
            allData: dischargingInfo,
            showPopup: showDischargePopup,
            updateBedStatus,
            closePop: () => setShowDischargePopup(false),
            saveBedStatus,
            saveReturnDayCase,
            reload: getFloorPlanListAPI,
          })}
      </Box>
    );
  }
);

// 使用 React.memo 优化，避免不必要的重新渲染
const QueueBar = memo(QueueBarComponent, (prevProps, nextProps) => {
  // 自定义比较函数，只在关键 props 变化时才重新渲染
  return (
    prevProps.showPatientName === nextProps.showPatientName &&
    prevProps.showRemarkBar === nextProps.showRemarkBar &&
    prevProps.bedStatusQueueList === nextProps.bedStatusQueueList &&
    prevProps.BsMlookupList === nextProps.BsMlookupList &&
    prevProps.getFloorPlanListAPI === nextProps.getFloorPlanListAPI &&
    prevProps.showQueueBar === nextProps.showQueueBar &&
    prevProps.setShowQueueBar === nextProps.setShowQueueBar &&
    prevProps.filter === nextProps.filter &&
    prevProps.readOnly === nextProps.readOnly
  );
});

export default QueueBar;
